import axios, { type AxiosError } from 'axios'
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import { useAtom } from "jotai"
import { isErrorResponse } from '@/helpers'
import type { Dayjs }  from 'dayjs'

import type {
    FormMutationOptions,
    YearlyFeeCollectionFormInput,
    YearlyFeeCollection,
    FeePayersGenerationResponse,
    MutationOptions,
    SendYearlyFeePaymentEmailsResponse,
} from '@/types'
import { selectedMunicipalityIdAtom } from "@/state"
import { convertToCents } from '@/helpers'
import dayjs from 'dayjs'

export function useYearlyFeeCollections(
    municipalityId: number,
    year: number,
) {
    return useQuery({
        queryKey: ['yearlyFeeCollections', municipalityId, year],
        queryFn: async () => {
            const { data } : { data: Array<YearlyFeeCollection>} = await axios.get(
                `/api/yearly_fee_collection/list?municipality_id=${municipalityId}&year=${year}`
            )
            return data
        },
    })
}

export function useYearlyFeeCollection(yearlyFeeCollectionId) {
    return useQuery({
        queryKey: ['yearlyFeeCollection', yearlyFeeCollectionId],
        queryFn: async () => {
            const { data } : { data: YearlyFeeCollection } = await axios.get(
                '/api/yearly_fee_collection/single?id=' + yearlyFeeCollectionId,
            )
            return data
        },
    })
}

export const useCreateYearlyFeeCollection = (options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const [municipalityId] = useAtom(selectedMunicipalityIdAtom)

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeeCollectionFormInput) => {            
            const queryParams = new URLSearchParams()
            queryParams.append('municipality_id', municipalityId.toString())
            queryParams.append('year', data.year.toString())
            queryParams.append('discounts_from_year', data.discounts_from_year.toString())
            queryParams.append('iban', data.iban)
            queryParams.append('fee_amount_cents', convertToCents(data.fee_amount).toString())
            // queryParams.append('collection_period', data.collection_period)
            queryParams.append('max_discount_cents', data.max_discount ? convertToCents(data.max_discount).toString() : '')

            return axios.post(`/api/yearly_fee_collection/create?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeCollections'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useUpdateYearlyFeeCollection = (yearlyFeeCollectionId, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        formDataId,
        setRequestInProgress,
        onErrorCallback,
    } = options

    return useMutation({
        mutationFn: (data: YearlyFeeCollectionFormInput) => {
            const queryParams = new URLSearchParams()
            queryParams.append('id', yearlyFeeCollectionId.toString())
            queryParams.append('iban', data.iban)
            queryParams.append('fee_amount_cents', convertToCents(data.fee_amount).toString())
            queryParams.append('max_discount_cents', data.max_discount ? convertToCents(data.max_discount).toString() : '')

            return axios.put(`/api/yearly_fee_collection/update?${queryParams.toString()}`)
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeCollections'] })
            queryClient.invalidateQueries({ queryKey: ['yearlyFeeCollection-' + yearlyFeeCollectionId] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useGenerateFeePayers = (
    yearlyFeeCollectionId: number,
    options: MutationOptions,
) => {

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: () => {            
            const queryParams = new URLSearchParams()

            queryParams.append('id', yearlyFeeCollectionId.toString())
            
            return axios.post(`/api/yearly_fee_collection/generate_fee_payers?${queryParams.toString()}`,
            )
        },
        onSuccess: response => {
            if (onSuccessCallback) {
                onSuccessCallback(response.data as FeePayersGenerationResponse)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export function useGenerateFeePayersStatus(jobId, keepRefetching) {
    return useQuery({
        queryKey: ['generateFeePayersStatus', jobId],
        queryFn: async () => {
            const { data } : { data: FeePayersGenerationResponse } = await axios.get(
                `/api/yearly_fee_collection/fee_payers_generation_status?fee_payers_generation_id=${jobId}`,
            )
            return data
        },
        refetchInterval: keepRefetching ? 2000 : undefined,
    })
}

export const useGenerateYearlyFeePayments = (
    yearlyFeeCollectionId: number,
    options: MutationOptions,
) => {

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: {
            dueDate: Dayjs,
            requestedAmountRatioPercent: string
            requestRestOfFeeFromPayers: boolean
        }) => {            
            const queryParams = new URLSearchParams()

            queryParams.append('id', yearlyFeeCollectionId.toString())
            queryParams.append('due_date', dayjs(data.dueDate).endOf('day').format('YYYYMMDD HH:mm'))
            if (data.requestRestOfFeeFromPayers) {
                queryParams.append('request_rest_of_fee_from_payers', 'true')
            } else {
                queryParams.append('request_rest_of_fee_from_payers', 'false')
                queryParams.append('requested_amount_ratio', (Number.parseFloat(data.requestedAmountRatioPercent)/100).toString())
            }
            
            return axios.post(`/api/yearly_fee_collection/generate_yearly_fee_payments?${queryParams.toString()}`,
            )
        },
        onSuccess: response => {
            if (onSuccessCallback) {
                onSuccessCallback(response.data as FeePayersGenerationResponse)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export function useGenerateYearlyFeePaymentsStatus(jobId, keepRefetching) {
    return useQuery({
        queryKey: ['generateYearlyFeePaymentsStatus', jobId],
        queryFn: async () => {
            const { data } : { data: FeePayersGenerationResponse } = await axios.get(
                `/api/yearly_fee_collection/yearly_fee_payments_generation_status?yearly_fee_payments_generation_id=${jobId}`,
            )
            return data
        },
        refetchInterval: keepRefetching ? 2000 : undefined,
    })
}

export const useSendYearlyFeePaymentEmails = (
    yearlyFeeCollectionId: number,
    options: MutationOptions,
) => {

    const {
        onSuccessCallback,
        onErrorCallback,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: () => {            
            const queryParams = new URLSearchParams()

            queryParams.append('id', yearlyFeeCollectionId.toString())
            
            return axios.post(`/api/yearly_fee_collection/send_yearly_fee_payment_emails?${queryParams.toString()}`,
            )
        },
        onSuccess: response => {
            if (onSuccessCallback) {
                onSuccessCallback(response.data as SendYearlyFeePaymentEmailsResponse)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export function useSendYearlyFeePaymentEmailsStatus(jobId, keepRefetching) {
    return useQuery({
        queryKey: ['sendYearlyFeePaymentEmailsStatus', jobId],
        queryFn: async () => {
            const { data } : { data: FeePayersGenerationResponse } = await axios.get(
                `/api/yearly_fee_collection/yearly_fee_payment_emails_generation_status?yearly_fee_payment_emails_generation_id=${jobId}`,
            )
            return data
        },
        refetchInterval: keepRefetching ? 2000 : undefined,
    })
}
